/*
 * Типы, зеркалящие сериализуемые DTO движка (`kernel/models.py`, `to_jsonable`).
 * Деньги/ставки приходят СТРОКАМИ (Decimal без потери точности на границе API,
 * §1.6). Числа парсим только для отображения/сортировки — не для денежной логики
 * (её во фронте нет, §1.2).
 */

export interface NormalizedSymbol {
  base: string
  quote: string
}

export interface ParserQuote {
  exchange: string
  /** Ставка фандинга — доля (строка). 0.0001 = 0.01%. */
  funding_rate: string
  /** Mark price (строка) или null, если ещё нет. */
  mark_price: string | null
  next_funding_ts_ms: number
  interval_sec: number
  /** 24h оборот в USDT (строка) или null, если биржа не отдаёт (эт.11). */
  volume_24h_usd: string | null
}

export interface ParserRow {
  coin: string
  symbol: NormalizedSymbol
  exchanges: string[]
  quotes: ParserQuote[]
  /** Наивный размах фандинга (max−min), доля-строка. Информационная метрика. */
  funding_spread: string
  /** Выбранная пара/направление и метрики возможности (OpportunityFinder, эт.11);
   *  null, если по монете нет проходной возможности. */
  chosen_long_exchange: string | null
  chosen_short_exchange: string | null
  net_funding_normalized: string | null
  score: string | null
  seconds_to_funding: number | null
}

export interface ParserSnapshot {
  rows: ParserRow[]
  exchanges: string[]
  common_pairs: number
  funding_highlight_count: number
  updated_ts_ms: number
}

/** Оповещение движка, доставленное по WS (`Event(kind="alert")`, §Этап 16).
 *  severity: 10=info, 20=warning, 30=critical (`AlertSeverity`). */
export interface AlertItem {
  code: string
  severity: number
  message: string
  ts_ms: number
}

export type EngineMode = 'dry_run' | 'live'
export type LifecycleState = 'stopped' | 'starting' | 'running' | 'stopping'
export type ExchangeHealth = 'healthy' | 'degraded' | 'unavailable'
export type HealthStatus = 'ok' | 'warn' | 'critical'
export type LinkStatus =
  | 'created'
  | 'entering'
  | 'active'
  | 'exiting'
  | 'flattening'
  | 'neutralizing'
  | 'stuck_directional'
  | 'closed'

/** Производный снимок открытой связки для таблицы «Активные сделки» (эт.17).
 *  «Текущий PNL» считает движок (единый PnL-источник эт.12) — фронт только показывает. */
export interface ActiveTrade {
  link_id: string
  coin: string
  status: LinkStatus
  long_exchange: string
  short_exchange: string
  symbol: NormalizedSymbol
  notional_usd: string
  leverage: string
  long_entry_price: string | null
  short_entry_price: string | null
  funding_rate_long: string | null
  funding_rate_short: string | null
  net_funding_normalized: string | null
  next_funding_ts_ms: number
  /** Момент следующего фандинга по каждой ноге отдельно (по-ножный отсчёт «До фандинга»). */
  next_funding_ts_ms_long: number
  next_funding_ts_ms_short: number
  current_pnl_usd: string
  current_pnl_pct: string
  /** Накопленный фандинг-доход. В 14-lite/dry-run быстром цикле фандинг не начисляется → «0». */
  funding_income_usd: string
  opened_ts_ms: number
}

/** Агрегаты блока «Статистика по сделкам» (§142). Считает движок в Decimal (§1.2),
 *  фронт только показывает: суммы — строки, счётчики — числа. */
export interface TradeStats {
  open_count: number
  current_pnl_usd: string
  closed_count: number
  profitable_count: number
  losing_count: number
  closed_pnl_usd: string
}

/** Карточка подключения биржи (панель «API-подключения», эт.17). */
export interface ExchangeConnection {
  exchange: string
  connected: boolean
  taker_rate: string | null
  maker_rate: string | null
  balance_usdt: string | null
  error: string | null
}

/** Одна health-проба (метрики — строки, как деньги; §Этап 16). */
export interface HealthProbe {
  name: string
  status: HealthStatus
  detail: string
  metrics: Record<string, string>
}

/** Снимок здоровья системы (баннеры/наблюдаемость, §Этап 16). */
export interface HealthSnapshot {
  overall: HealthStatus
  entry_blocked: boolean
  watchdog_stale: boolean
  probes: HealthProbe[]
  ts_ms: number
}

/** Запись истории закрытой связки (`ClosedTradeRecord`). Цены входа/выхода и доход с
 *  фандинга — для колонок §146; `mode` — пометка dry/live (§388). Деньги/цены — строки. */
export interface ClosedTrade {
  link_id: string
  coin: string
  long_exchange: string
  short_exchange: string
  opened_ts_ms: number
  closed_ts_ms: number
  realized_pnl: string
  fees: string
  expected_net: string
  exit_reason: string
  notional_usd: string
  mode: EngineMode
  long_entry_price: string | null
  short_entry_price: string | null
  long_exit_price: string | null
  short_exit_price: string | null
  funding_income: string
}

/** Конфиг бота как jsonable-словарь (форма настроек §1.11): деньги/ставки — строки,
 *  целые — числа, enum — строки, списки — массивы. Поле-агностично (строится по ключам). */
export type BotConfigDto = Record<string, string | number | boolean | string[] | null>

/** Снимок состояния движка (`EngineState`). Парсер-странице нужны режим/idle/exchanges;
 *  «Авто-бот» (эт.17) использует active_trades/connections/health/lifecycle/safe_startup. */
export interface EngineState {
  mode: EngineMode
  running: boolean
  entry_enabled: boolean
  lifecycle: LifecycleState
  links: unknown[]
  active_trades: ActiveTrade[]
  trade_stats: TradeStats | null
  connections: ExchangeConnection[]
  exchange_health: Record<string, ExchangeHealth>
  exchanges: string[]
  candidate_count: number
  opportunity_count: number
  /** §104: монотонный счётчик REST-запросов бота на биржи (для индикатора API-активности). */
  exchange_request_count: number
  idle: boolean
  idle_reason: string | null
  safe_startup: boolean
  /** Item 6: halt открытия новых сделок по всему боту (STUCK / отказ персиста / упавший
   *  владелец связки). running/entry_enabled могут быть true, но входов НЕ будет. */
  halted: boolean
  halt_reason: string | null
  health: HealthSnapshot | null
  started_ts_ms: number
  snapshot_ts_ms: number
}

/** Идемпотентные команды UI→движок (`CommandKind`). */
export const CommandKind = {
  START: 'start',
  STOP: 'stop',
  EMERGENCY_CLOSE: 'emergency_close',
  TOGGLE_ENTRY: 'toggle_entry',
  SAVE_CONFIG: 'save_config',
  CONNECT: 'connect',
  RESET: 'reset',
  CLEAR_LOG: 'clear_log',
  CLEAR_HISTORY: 'clear_history',
  REFRESH_PARSER: 'refresh_parser',
  RESUME_AFTER_SAFE_STARTUP: 'resume_after_safe_startup',
} as const

export type CommandKind = (typeof CommandKind)[keyof typeof CommandKind]

/** Конверт WS: первым приходит снимок состояния, затем поток событий (§1.3). */
export interface WsSnapshotMessage {
  kind: 'snapshot'
  payload: EngineState
}

export interface WsEventMessage {
  kind: string
  ts_ms: number
  payload: Record<string, unknown>
}

export type WsMessage = WsSnapshotMessage | WsEventMessage
